/**
 * Rateio entre centros de custo (ADR 0021, evolução aprovada; primeiro uso no Jurídico, ADR 0029).
 * Sem rateio: um centro de custo principal (100%). Com rateio: vários centros, cada um com o seu
 * percentual (até duas casas), somando exatamente 100%. Departamento e pessoa são critérios
 * auxiliares de cada linha; o vínculo é sempre com o centro de custo. Reutilizável por Compras.
 */
import { z } from 'zod';

const PERCENT_RE = /^\d{1,3}(\.\d{1,2})?$/;
const optionalId = z
  .uuid()
  .or(z.literal(''))
  .transform((v) => (v === '' ? null : v))
  .nullable()
  .optional();

export const costAllocationLine = z.object({
  costCenterId: z.uuid('Escolha o centro de custo'),
  percent: z.string().trim().regex(PERCENT_RE, 'Percentual com até duas casas (ex.: 33.33)'),
  departmentId: optionalId,
  userId: optionalId,
  /** Valor da linha, calculado e gravado pelo servidor no momento (nunca recalculado depois). */
  amount: z
    .string()
    .regex(/^\d{1,15}(\.\d{1,4})?$/)
    .optional(),
});
export const costAllocationInput = z.object({
  split: z.boolean(),
  lines: z.array(costAllocationLine).max(30),
  /** Moeda dos valores das linhas (sem valor conhecido, o rateio é só percentual). */
  currency: z
    .string()
    .regex(/^[A-Z]{3}$/)
    .optional(),
});
export type CostAllocation = z.infer<typeof costAllocationInput>;

/** Percentual em centésimos (33.33 → 3333), sem ponto flutuante. */
function hundredths(percent: string): number {
  const [int, dec = ''] = percent.split('.');
  return Number(int) * 100 + Number(dec.padEnd(2, '0'));
}

/**
 * O que está errado no rateio (vazio = válido). `required`: precisa de pelo menos um centro.
 * Caminhos no formato allocation.N.campo para a tela marcar a linha certa.
 */
export function costAllocationIssues(
  a: CostAllocation | null | undefined,
  required: boolean,
): { path: string; message: string }[] {
  const issues: { path: string; message: string }[] = [];
  const lines = a?.lines ?? [];
  if (lines.length === 0) {
    if (required) issues.push({ path: 'allocation', message: 'Escolha o centro de custo' });
    return issues;
  }
  if (!a!.split && lines.length !== 1) {
    issues.push({ path: 'allocation', message: 'Sem rateio, informe um centro de custo só' });
  }
  if (a!.split && lines.length < 2) {
    issues.push({ path: 'allocation', message: 'Com rateio, informe pelo menos dois centros' });
  }
  const seen = new Set<string>();
  let total = 0;
  lines.forEach((l, i) => {
    const h = PERCENT_RE.test(l.percent) ? hundredths(l.percent) : -1;
    if (h <= 0 || h > 10000) {
      issues.push({ path: `allocation.${i}.percent`, message: 'Percentual entre 0,01 e 100' });
    } else total += h;
    if (seen.has(l.costCenterId)) {
      issues.push({ path: `allocation.${i}.costCenterId`, message: 'Centro de custo repetido' });
    }
    seen.add(l.costCenterId);
  });
  if (total !== 10000 && !issues.some((x) => x.path.endsWith('.percent'))) {
    issues.push({
      path: 'allocation',
      message: `Os percentuais somam ${(total / 100).toFixed(2)}%; precisam somar 100%`,
    });
  }
  return issues;
}

/**
 * Valor de cada linha a partir do valor total (texto decimal, até 4 casas): arredonda em
 * centavos e a diferença do arredondamento fica na maior linha, para a soma bater com o total.
 */
export function costAllocationAmounts(amount: string, lines: { percent: string }[]): string[] {
  if (!/^\d{1,15}(\.\d{1,4})?$/.test(amount) || lines.length === 0) return lines.map(() => '');
  const [int, dec = ''] = amount.split('.');
  const cents = BigInt(int!) * 100n + BigInt(dec.padEnd(4, '0').slice(0, 2));
  const parts = lines.map((l) =>
    PERCENT_RE.test(l.percent) ? (cents * BigInt(hundredths(l.percent))) / 10000n : 0n,
  );
  const diff = cents - parts.reduce((s, x) => s + x, 0n);
  const biggest = lines.reduce(
    (best, l, i) => (hundredths(l.percent) > hundredths(lines[best]!.percent) ? i : best),
    0,
  );
  if (
    lines.reduce((s, l) => s + (PERCENT_RE.test(l.percent) ? hundredths(l.percent) : 0), 0) ===
    10000
  ) {
    parts[biggest] = parts[biggest]! + diff;
  }
  return parts.map((c) => `${c / 100n}.${String(c % 100n).padStart(2, '0')}`);
}

/**
 * Fotografia do rateio na gravação: com valor conhecido, cada linha guarda o seu valor e a moeda;
 * sem valor, fica só o percentual (nenhum valor fictício). A versão anterior fica na auditoria.
 */
export function costAllocationSnapshot(
  a: CostAllocation,
  amount: string | null | undefined,
  currency: string | null | undefined,
): CostAllocation {
  const lines = a.lines.map((l) => {
    const { amount: _old, ...rest } = l;
    return rest;
  });
  if (!amount || !currency) return { split: a.split, lines };
  const values = costAllocationAmounts(amount, lines);
  return {
    split: a.split,
    currency,
    lines: lines.map((l, i) => (values[i] ? { ...l, amount: values[i] } : l)),
  };
}

/** Centro de custo principal (o de maior percentual), para listas e indicadores. */
export function mainCostCenter(a: CostAllocation | null | undefined): string | null {
  if (!a?.lines.length) return null;
  return a.lines.reduce((best, l) => (hundredths(l.percent) > hundredths(best.percent) ? l : best))
    .costCenterId;
}
